import { useQuery } from '@tanstack/react-query';
import { darkTheme } from '../../lib/tokens';
import { api } from '../../lib/api';

interface AggRow {
  key: string;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  calls: number;
}
interface DailyPoint {
  date: string;
  costUsd: number;
  calls: number;
}
interface Forecast {
  monthToDateUsd: number;
  avgDailyUsd7d: number;
  projectedMonthUsd: number;
  monthlyBudgetUsd: number;
  headroomDays: number | null;
  severity: 'ok' | 'warn' | 'crit';
}
interface AnthropicDetails {
  rangeDays: number;
  totalCostUsd: number;
  totalCalls: number;
  cacheHitRate: number;
  byModel: AggRow[];
  bySource: AggRow[];
  daily14d: DailyPoint[];
  forecast: Forecast;
  generatedAt: string;
}

function fetchDetails(): Promise<AnthropicDetails> {
  return api.get<AnthropicDetails>('/admin/health/anthropic/details');
}

export function AnthropicDetailsSection(): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ['health', 'anthropic-details'],
    queryFn: fetchDetails,
    refetchInterval: 5 * 60_000,
  });

  if (isLoading) return <Box>Lädt …</Box>;
  if (error) {
    return (
      <Box dangerous>
        Fehler: {error instanceof Error ? error.message : 'unbekannt'}
      </Box>
    );
  }
  if (!data) return <Box>Keine Daten.</Box>;

  return (
    <article
      className="rounded-md border p-4"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <header className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-text-primary">Anthropic — Details</h3>
        <span className="text-[11px] text-text-tertiary">
          {data.totalCalls.toLocaleString('de-DE')} Calls · letzte {data.rangeDays} Tage
        </span>
      </header>

      <ForecastBlock forecast={data.forecast} />

      <section className="mb-4">
        <SectionTitle>Tagesverlauf — letzte 14 Tage</SectionTitle>
        <DailySparkline data={data.daily14d} />
      </section>

      <div className="grid gap-4 md:grid-cols-2">
        <BreakdownTable
          title="Pro Modell"
          rows={data.byModel}
          totalCostUsd={data.totalCostUsd}
          formatKey={shortenModelId}
        />
        <BreakdownTable
          title="Pro Source"
          rows={data.bySource}
          totalCostUsd={data.totalCostUsd}
        />
      </div>

      <footer
        className="mt-4 flex flex-wrap justify-between gap-2 border-t pt-2 text-[10px] text-text-tertiary"
        style={{ borderColor: darkTheme.colors.borderSubtle }}
      >
        <span>
          Cache-Hit-Rate: <span className="tabular text-text-primary">{(data.cacheHitRate * 100).toFixed(1)}%</span>
          {' '}(Anteil cache_read an Input-Tokens)
        </span>
        <span>
          Stand: {new Date(data.generatedAt).toLocaleString('de-DE', { timeZone: 'Europe/Berlin' })}
        </span>
      </footer>
    </article>
  );
}

function ForecastBlock({ forecast }: { forecast: Forecast }): JSX.Element {
  const pct = (forecast.projectedMonthUsd / forecast.monthlyBudgetUsd) * 100;
  const color =
    forecast.severity === 'crit'
      ? darkTheme.colors.danger
      : forecast.severity === 'warn'
      ? darkTheme.colors.warning
      : darkTheme.colors.success;

  return (
    <section className="mb-4">
      <SectionTitle>Budget-Forecast (laufender Monat)</SectionTitle>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Bisher diesen Monat"
          value={`$${forecast.monthToDateUsd.toFixed(2)}`}
          hint={`von $${forecast.monthlyBudgetUsd.toFixed(0)} Budget`}
        />
        <Stat
          label="Tagesschnitt 7d"
          value={`$${forecast.avgDailyUsd7d.toFixed(2)}`}
          hint="Burn-Rate"
        />
        <Stat
          label="Hochrechnung Monatsende"
          value={`$${forecast.projectedMonthUsd.toFixed(2)}`}
          hint={`${pct.toFixed(0)}% vom Budget`}
          color={color}
        />
        <Stat
          label="Budget reicht noch"
          value={
            forecast.headroomDays == null
              ? '—'
              : forecast.headroomDays > 60
              ? '> 60 Tage'
              : `${forecast.headroomDays.toFixed(1)} Tage`
          }
          hint="bei aktueller Burn-Rate"
          color={
            forecast.headroomDays != null && forecast.headroomDays < 7
              ? darkTheme.colors.danger
              : undefined
          }
        />
      </div>
      <div className="mt-2">
        <div
          className="relative h-2 overflow-hidden rounded"
          style={{ background: darkTheme.colors.bgElevated }}
        >
          <div
            className="h-full rounded"
            style={{
              width: `${Math.min(100, pct)}%`,
              background: color,
              transition: 'width 0.3s ease',
            }}
          />
          {pct > 100 && (
            <span
              className="absolute right-1 top-1/2 -translate-y-1/2 text-[9px] font-bold"
              style={{ color: darkTheme.colors.danger }}
            >
              +{(pct - 100).toFixed(0)}% Overshoot
            </span>
          )}
        </div>
      </div>
    </section>
  );
}

function DailySparkline({ data }: { data: DailyPoint[] }): JSX.Element {
  const max = Math.max(...data.map((d) => d.costUsd), 0.01);
  return (
    <div className="flex items-end gap-1" style={{ height: 64 }}>
      {data.map((d) => {
        const h = Math.max(2, (d.costUsd / max) * 60);
        return (
          <div
            key={d.date}
            title={`${d.date}: $${d.costUsd.toFixed(2)} · ${d.calls} calls`}
            className="flex-1 rounded-t"
            style={{
              height: h,
              background: darkTheme.colors.accent,
              opacity: d.costUsd > 0 ? 1 : 0.2,
            }}
          />
        );
      })}
    </div>
  );
}

function BreakdownTable({
  title,
  rows,
  totalCostUsd,
  formatKey,
}: {
  title: string;
  rows: AggRow[];
  totalCostUsd: number;
  formatKey?: (key: string) => string;
}): JSX.Element {
  if (rows.length === 0) {
    return (
      <section>
        <SectionTitle>{title}</SectionTitle>
        <p className="text-xs text-text-tertiary">Keine Daten.</p>
      </section>
    );
  }
  return (
    <section>
      <SectionTitle>{title}</SectionTitle>
      <div className="space-y-1">
        {rows.map((r) => {
          const pct = totalCostUsd > 0 ? (r.costUsd / totalCostUsd) * 100 : 0;
          return (
            <div key={r.key} className="text-xs">
              <div className="mb-0.5 flex items-baseline justify-between gap-2">
                <span className="truncate text-text-secondary" title={r.key}>
                  {formatKey ? formatKey(r.key) : r.key}
                </span>
                <span className="tabular text-text-primary">
                  ${r.costUsd.toFixed(2)} · {pct.toFixed(0)}%
                </span>
              </div>
              <div
                className="h-1 overflow-hidden rounded"
                style={{ background: darkTheme.colors.bgElevated }}
              >
                <div
                  className="h-full"
                  style={{
                    width: `${Math.min(100, pct)}%`,
                    background: darkTheme.colors.accent,
                  }}
                />
              </div>
              <div className="mt-0.5 text-[10px] text-text-tertiary">
                {r.calls.toLocaleString('de-DE')} calls · {(r.inputTokens / 1000).toFixed(0)}k in · {(r.outputTokens / 1000).toFixed(0)}k out
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Stat({
  label,
  value,
  hint,
  color,
}: {
  label: string;
  value: string;
  hint?: string;
  color?: string;
}): JSX.Element {
  return (
    <div
      className="rounded-md border p-2"
      style={{ borderColor: darkTheme.colors.borderSubtle }}
    >
      <div className="text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
        {label}
      </div>
      <div
        className="mt-0.5 text-base font-bold tabular"
        style={{ color: color ?? darkTheme.colors.textPrimary }}
      >
        {value}
      </div>
      {hint && <div className="text-[10px] text-text-tertiary">{hint}</div>}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
      {children}
    </div>
  );
}

function Box({ children, dangerous }: { children: React.ReactNode; dangerous?: boolean }): JSX.Element {
  return (
    <div
      className="rounded-md border p-6 text-sm"
      style={{
        background: dangerous ? darkTheme.colors.dangerBg : darkTheme.colors.bgSurface,
        color: dangerous ? darkTheme.colors.danger : darkTheme.colors.textTertiary,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      {children}
    </div>
  );
}

// "claude-sonnet-4-20250514" → "Sonnet-4 (2025-05-14)"
function shortenModelId(id: string): string {
  const m = id.match(/claude-(sonnet|haiku|opus)-(\d+(?:-\d+)?)(?:-(\d{8}))?/);
  if (!m) return id;
  const family = m[1]!.charAt(0).toUpperCase() + m[1]!.slice(1);
  const version = m[2]!.replace(/-/g, '.');
  const date = m[3];
  if (!date) return `${family}-${version}`;
  return `${family}-${version} (${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)})`;
}
